import { useEffect, useState } from 'react'
import {
  CheckCircle2, XCircle, Clock, RefreshCw,
  Loader2, AlertTriangle, ReceiptText, ExternalLink,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import AdminLayout from '@/components/admin/AdminLayout'
import Button from '@/components/ui/Button'

// ── Helpers ──────────────────────────────────────────────────────────────────

const peso = (n) =>
  n != null && !isNaN(n)
    ? '₱' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 2 })
    : '—'

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-PH', {
    month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

const TYPE_LABEL = {
  downpayment: 'Downpayment',
  balance:     'Balance',
  full:        'Full Payment',
}

const STATUS_UI = {
  pending:  { label: 'Pending Verification', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: Clock         },
  verified: { label: 'Verified',             color: 'text-green-400 bg-green-500/10 border-green-500/25',   icon: CheckCircle2  },
  rejected: { label: 'Rejected',             color: 'text-siglat-red bg-siglat-red/10 border-siglat-red/25', icon: XCircle      },
}

const FILTER_OPTS = [
  { val: '',         label: 'All'      },
  { val: 'pending',  label: 'Pending'  },
  { val: 'verified', label: 'Verified' },
  { val: 'rejected', label: 'Rejected' },
]

// ── Receipt Row ───────────────────────────────────────────────────────────────

function ReceiptRow({ receipt, onAction }) {
  const adminProfile = useAuthStore((s) => s.profile)
  const [imgUrl,        setImgUrl]        = useState(null)
  const [showImg,       setShowImg]       = useState(false)
  const [rejNote,       setRejNote]       = useState('')
  const [showRejInput,  setShowRejInput]  = useState(false)
  const [acting,        setActing]        = useState(false)
  const [err,           setErr]           = useState(null)

  useEffect(() => {
    if (!receipt.receipt_image_path) return
    supabase.storage
      .from('receipts')
      .download(receipt.receipt_image_path)
      .then(({ data, error }) => {
        if (error || !data) { console.error('Receipt img error:', error); return }
        setImgUrl(URL.createObjectURL(data))
      })
    return () => { if (imgUrl) URL.revokeObjectURL(imgUrl) }
  }, [receipt.receipt_image_path]) // eslint-disable-line

  async function verify() {
    setActing(true)
    setErr(null)
    const now = new Date().toISOString()
    const { error } = await supabase
      .from('payment_receipts')
      .update({ status: 'verified', verified_by: adminProfile.id, verified_at: now, rejection_note: null })
      .eq('id', receipt.id)
    if (error) { setErr('Failed to verify.'); setActing(false); return }

    // Auto-advance booking to 'confirmed' so it appears in the Dispatch queue.
    // booking_id may be null on new receipts — resolve via proposal_id if so.
    let bookingId = receipt.booking_id
    if (!bookingId && receipt.proposal_id) {
      const { data: fp } = await supabase
        .from('final_proposals')
        .select('booking_id')
        .eq('id', receipt.proposal_id)
        .single()
      bookingId = fp?.booking_id ?? null
    }
    if (bookingId) {
      await supabase
        .from('bookings')
        .update({ status: 'confirmed' })
        .eq('id', bookingId)
        .in('status', ['pending'])
    }

    supabase.functions.invoke('send-notification', { body: {
      type: 'payment_verified', customer_id: receipt.customer_id,
      data: { ref_number: receipt._bookingRef, amount: receipt.amount_stated ? `PHP ${Number(receipt.amount_stated).toLocaleString()}` : null },
    }}).catch(console.error)
    onAction(receipt.id, 'verified')
    setActing(false)
  }

  async function reject() {
    if (!rejNote.trim()) { setErr('Please enter a rejection reason.'); return }
    setActing(true)
    setErr(null)
    const { error } = await supabase
      .from('payment_receipts')
      .update({ status: 'rejected', rejection_note: rejNote.trim(), verified_by: adminProfile.id, verified_at: new Date().toISOString() })
      .eq('id', receipt.id)
    if (error) { setErr('Failed to reject.'); setActing(false); return }
    onAction(receipt.id, 'rejected')
    setActing(false)
    setShowRejInput(false)
  }

  const ui = STATUS_UI[receipt.status] ?? STATUS_UI.pending
  const Icon = ui.icon

  return (
    <div className="bg-surface-raised border border-surface-border rounded-xl overflow-hidden">

      {/* Summary */}
      <div className="px-4 py-3.5 flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-semibold text-ink-primary">{receipt._customerName ?? 'Customer'}</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium flex items-center gap-1 ${ui.color}`}>
              <Icon className="w-3 h-3" />
              {ui.label}
            </span>
          </div>
          <p className="text-xs text-ink-muted font-mono mb-0.5">{receipt._bookingRef ?? '—'}</p>
          <div className="flex items-center gap-3 flex-wrap text-xs text-ink-muted">
            <span>{TYPE_LABEL[receipt.receipt_type] ?? receipt.receipt_type}</span>
            <span className="font-semibold text-ink-primary">{peso(receipt.amount_stated)}</span>
            <span>{fmtDate(receipt.uploaded_at)}</span>
          </div>
          {receipt.payment_reference && (
            <p className="text-xs text-ink-muted mt-0.5">Ref: <span className="text-ink-secondary font-mono">{receipt.payment_reference}</span></p>
          )}
          {receipt.status === 'rejected' && receipt.rejection_note && (
            <p className="text-xs text-siglat-red mt-1">Rejected: {receipt.rejection_note}</p>
          )}
        </div>

        {/* Receipt image thumbnail */}
        {imgUrl && (
          <button
            onClick={() => setShowImg(!showImg)}
            className="shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-surface-border hover:border-siglat-teal transition-colors"
          >
            <img src={imgUrl} alt="Receipt" className="w-full h-full object-cover" />
          </button>
        )}
      </div>

      {/* Full image */}
      {showImg && imgUrl && (
        <div className="px-4 pb-3">
          <div className="relative rounded-xl overflow-hidden border border-surface-border">
            <img src={imgUrl} alt="Receipt full" className="w-full object-contain max-h-80 bg-surface-overlay" />
            <a
              href={imgUrl}
              download={`receipt-${receipt.id?.slice(0,8)}.jpg`}
              className="absolute top-2 right-2 bg-black/60 rounded-full p-1.5"
            >
              <ExternalLink className="w-3.5 h-3.5 text-white" />
            </a>
          </div>
        </div>
      )}

      {/* Actions — only for pending */}
      {receipt.status === 'pending' && (
        <div className="border-t border-surface-border px-4 py-3 flex flex-col gap-2">
          {err && (
            <div className="flex items-center gap-2 p-2 bg-siglat-red/10 border border-siglat-red/25 rounded-lg">
              <AlertTriangle className="w-3.5 h-3.5 text-siglat-red shrink-0" />
              <p className="text-xs text-siglat-red">{err}</p>
            </div>
          )}

          {!showRejInput ? (
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={verify}
                disabled={acting}
                className="flex-1"
              >
                {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Verify
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowRejInput(true)}
                disabled={acting}
                className="flex-1"
              >
                <XCircle className="w-3.5 h-3.5" />
                Reject
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <textarea
                value={rejNote}
                onChange={(e) => setRejNote(e.target.value)}
                rows={2}
                placeholder="Reason for rejection (e.g. amount mismatch, invalid receipt)…"
                className="w-full bg-surface-overlay border border-surface-border rounded-lg px-3 py-2 text-sm text-ink-primary placeholder:text-ink-muted resize-none focus:outline-none focus:ring-1 focus:ring-siglat-red"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={reject} disabled={acting} className="flex-1 bg-siglat-red hover:bg-siglat-red/80">
                  {acting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm Reject'}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setShowRejInput(false); setErr(null) }} className="flex-1">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function AdminPayments() {
  const [receipts,   setReceipts]   = useState([])
  const [loading,    setLoading]    = useState(true)
  const [filter,     setFilter]     = useState('')
  const [refreshing, setRefreshing] = useState(false)

  async function fetchReceipts() {
    setLoading(true)

    let q = supabase
      .from('payment_receipts')
      .select('id, proposal_id, receipt_type, amount_stated, payment_reference, status, rejection_note, receipt_image_path, uploaded_at, customer_id, booking_id')
      .order('uploaded_at', { ascending: false })

    if (filter) q = q.eq('status', filter)

    const { data: rows, error } = await q
    if (error || !rows?.length) { setReceipts([]); setLoading(false); return }

    // Batch fetch customer names and booking refs
    const customerIds = [...new Set(rows.map((r) => r.customer_id).filter(Boolean))]
    const bookingIds  = [...new Set(rows.map((r) => r.booking_id).filter(Boolean))]

    const [profileRes, bookingRes] = await Promise.all([
      customerIds.length
        ? supabase.from('profiles').select('id, full_name').in('id', customerIds)
        : { data: [] },
      bookingIds.length
        ? supabase.from('bookings').select('id, ref_number').in('id', bookingIds)
        : { data: [] },
    ])

    const profileMap = {}
    for (const p of profileRes.data ?? []) profileMap[p.id] = p

    const bookingMap = {}
    for (const b of bookingRes.data ?? []) bookingMap[b.id] = b

    const enriched = rows.map((r) => ({
      ...r,
      _customerName: profileMap[r.customer_id]?.full_name ?? 'Unknown',
      _bookingRef:   bookingMap[r.booking_id]?.ref_number ?? '—',
    }))

    setReceipts(enriched)
    setLoading(false)
  }

  useEffect(() => { fetchReceipts() }, [filter]) // eslint-disable-line

  async function handleRefresh() {
    setRefreshing(true)
    await fetchReceipts()
    setRefreshing(false)
  }

  function handleAction(id, newStatus) {
    setReceipts((prev) => prev.map((r) => r.id === id ? { ...r, status: newStatus } : r))
  }

  const pendingCount = receipts.filter((r) => r.status === 'pending').length

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 max-w-4xl mx-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Payment Receipts</h1>
            <p className="text-sm text-ink-muted mt-0.5">
              {pendingCount > 0
                ? <span className="text-yellow-400 font-medium">{pendingCount} pending verification</span>
                : 'All receipts verified'}
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-ink-muted hover:text-ink-primary hover:bg-surface-overlay rounded-lg transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
          {FILTER_OPTS.map((opt) => (
            <button
              key={opt.val}
              onClick={() => setFilter(opt.val)}
              className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition-colors
                ${filter === opt.val
                  ? 'bg-siglat-teal/15 border-siglat-teal text-siglat-teal font-semibold'
                  : 'bg-surface-overlay border-surface-border text-ink-muted hover:text-ink-primary'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="flex flex-col gap-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-24 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
            ))}
          </div>
        ) : receipts.length === 0 ? (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-10 text-center">
            <ReceiptText className="w-8 h-8 text-ink-muted mx-auto mb-2 opacity-30" />
            <p className="text-sm text-ink-muted">No payment receipts found.</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {receipts.map((r) => (
              <ReceiptRow key={r.id} receipt={r} onAction={handleAction} />
            ))}
          </div>
        )}

      </div>
    </AdminLayout>
  )
}
